import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Professional Light Theme Design System
const DESIGN_SYSTEM = {
  spacing: {
    xs: 'space-y-2',
    sm: 'space-y-3', 
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  },
  container: {
    sm: 'max-w-4xl',
    md: 'max-w-5xl', 
    lg: 'max-w-6xl',
    xl: 'max-w-7xl'
  },
  padding: {
    sm: 'p-4 md:p-6',
    md: 'p-6 md:p-8',
    lg: 'p-8 md:p-10',
    xl: 'p-10 md:p-12'
  },
  text: {
    badge: 'text-sm font-semibold tracking-wide',
    heading1: 'text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight',
    heading2: 'text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight',
    heading3: 'text-2xl md:text-3xl font-bold tracking-tight',
    body: 'text-lg md:text-xl leading-relaxed',
    caption: 'text-base md:text-lg leading-relaxed'
  },
  cards: {
    sm: 'rounded-2xl bg-white/60 backdrop-blur-lg border border-white/40 shadow-lg shadow-black/5',
    md: 'rounded-3xl bg-white/60 backdrop-blur-lg border border-white/40 shadow-lg shadow-black/5',
    lg: 'rounded-3xl bg-white/70 backdrop-blur-xl border border-white/50 shadow-xl shadow-black/10'
  },
  buttons: {
    primary: 'bg-gradient-to-r from-ripple-blue to-ripple-blue-light hover:from-ripple-blue/90 hover:to-ripple-blue-light/90 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-ripple-blue/25 border border-ripple-blue/30',
    secondary: 'bg-white/60 hover:bg-white/80 backdrop-blur-lg border border-white/40 hover:border-white/60 text-slate-800 font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-black/10'
  }
};

// Professional Web3 Icons
const RippleIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/icons/partners/ripple.svg" 
    alt="Ripple" 
    className={className}
  />
);

const WalletIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M21 18v1c0 1.1-.9 2-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14c1.1 0 2 .9 2 2v1h-9a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h9zM12 16h10V8H12v8zm4-2.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
  </svg>
);

const ChainlinkIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/icons/partners/chainlink.svg" 
    alt="Chainlink" 
    className={className}
  />
);

const EthereumIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/icons/partners/ethereum.svg" 
    alt="Ethereum" 
    className={className}
    style={{ 
      filter: 'brightness(0) saturate(100%) invert(0%)',
    }}
  />
);

const ArbitrumIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/icons/partners/arbitrum.svg" 
    alt="Arbitrum" 
    className={className}
  />
);

const BNBIcon = ({ className = "w-6 h-6" }) => (
  <img 
    src="/icons/partners/BNB.svg" 
    alt="BNB Chain" 
    className={className}
  />
);

const Partners: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Check if the device is mobile (width < 768px)
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };
    
    checkIsMobile();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkIsMobile);
      
      return () => window.removeEventListener('resize', checkIsMobile);
    }
  }, []);

  const partners = [
    { 
      name: "Ripple Labs", 
      icon: <RippleIcon className="w-8 h-8 md:w-10 md:h-10" />,
      color: "ripple-blue"
    },
    { 
      name: "XUMM", 
      icon: <WalletIcon className="w-8 h-8 md:w-10 md:h-10 text-slate-700" />,
      color: "accent-blue"
    },
    { 
      name: "Chainlink", 
      icon: <ChainlinkIcon className="w-8 h-8 md:w-10 md:h-10" />,
      color: "ripple-teal"
    },
    { 
      name: "Ethereum", 
      icon: <EthereumIcon className="w-8 h-8 md:w-10 md:h-10" />,
      color: "accent-sky"
    },
    { 
      name: "Arbitrum", 
      icon: <ArbitrumIcon className="w-8 h-8 md:w-10 md:h-10" />,
      color: "accent-cyan"
    },
    { 
      name: "BNB Chain", 
      icon: <BNBIcon className="w-8 h-8 md:w-10 md:h-10" />,
      color: "ripple-blue"
    },
  ];

  return (
    <section id="partners" className="relative py-8 md:py-12 lg:py-16 overflow-hidden">
      {/* Enhanced Background - Optimized for mobile */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100"></div>
        
        {/* Floating Glass Orbs - Reduced on mobile */}
        <div className={`absolute inset-0 ${isMobile ? 'opacity-30' : 'opacity-100'}`}>
          <div className={`absolute w-80 h-80 rounded-full bg-gradient-to-br from-ripple-blue/5 to-transparent ${!isMobile ? 'backdrop-blur-3xl animate-float-slow' : ''} top-20 -right-20`}></div>
          <div className={`absolute w-60 h-60 rounded-full bg-gradient-to-br from-accent-blue/5 to-transparent ${!isMobile ? 'backdrop-blur-3xl animate-float-slower' : ''} bottom-20 -left-20`}></div>
        </div>
        
        {/* Technical Grid Pattern - Simplified on mobile */}
        <div className={`absolute inset-0 ${isMobile ? 'opacity-[0.01]' : 'opacity-[0.02]'}`} style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        {/* Subtle Noise Texture - Desktop only */}
        {!isMobile && (
          <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='1' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}></div>
        )}
      </div>

      <div className={`${DESIGN_SYSTEM.container.xl} mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10`}>
        
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-12 md:mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Main Heading */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
            Trusted ecosystem
            <br />
            <span className="bg-gradient-to-r from-ripple-blue to-accent-blue bg-clip-text text-transparent">
              partners.
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Join our growing ecosystem of partners bringing real finance to the XRP economy.
          </p>
        </motion.div>

        {/* Partners Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mb-12">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.name}
              className="group relative"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={!isMobile ? { 
                y: -8, 
                scale: 1.02,
              } : undefined}
            >
              <div className={`relative ${DESIGN_SYSTEM.cards.lg} ${DESIGN_SYSTEM.padding.md} aspect-square min-h-[120px] md:min-h-[140px] flex flex-col items-center justify-center group-hover:bg-white/80 group-hover:border-white/70 transition-all duration-300 ${!isMobile ? 'group-hover:shadow-2xl group-hover:shadow-ripple-blue/10' : ''}`}>
                
                {/* Inner Glow Effect - Desktop only */}
                {!isMobile && (
                  <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/40 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}

                {/* Subtle Shimmer - Desktop only */}
                {!isMobile && (
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-500"></div>
                )}

                {/* Partner Icon */}
                <motion.div
                  className={`relative flex items-center justify-center mb-4 ${!isMobile ? 'group-hover:scale-110' : ''} transition-transform duration-300`}
                  whileHover={!isMobile ? { rotate: [0, 5, -5, 0] } : undefined}
                  transition={{ duration: 0.6 }}
                >
                  {partner.icon}
                </motion.div>

                {/* Partner Name */}
                <motion.p
                  className="text-sm md:text-base font-semibold text-slate-700 group-hover:text-slate-900 transition-colors duration-300 text-center"
                  initial={{ opacity: 0, y: 10 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                >
                  {partner.name}
                </motion.p>

                {/* Modern Pulse Ring Animation - Simplified on mobile */}
                <motion.div
                  className="absolute inset-0 rounded-3xl"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {/* Animated Pulse Ring - Reduced on mobile */}
                  <motion.div
                    className={`absolute inset-0 rounded-3xl border ${isMobile ? 'border-ripple-blue/10' : 'border-ripple-blue/20'}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ 
                      duration: isMobile ? 0.8 : 1.2, 
                      delay: index * 0.15,
                      ease: "easeOut"
                    }}
                  />
                  {/* Subtle Glow Effect - Desktop only */}
                  {!isMobile && (
                    <motion.div
                      className="absolute inset-0 rounded-3xl bg-gradient-to-br from-ripple-blue/5 via-transparent to-accent-blue/5"
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{ 
                        duration: 1.4, 
                        delay: index * 0.15 + 0.2,
                        ease: "easeOut"
                      }}
                    />
                  )}
                  {/* Corner Accent Dots - Desktop only */}
                  {!isMobile && (
                    <motion.div
                      className="absolute top-2 right-2 w-1.5 h-1.5 bg-ripple-blue rounded-full"
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 0.6 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: index * 0.1 + 0.8,
                        ease: "easeOut"
                      }}
                    />
                  )}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          {/* Desktop version with full animations */}
          {!isMobile ? (
            <motion.button
              className={`${DESIGN_SYSTEM.buttons.secondary} px-12 py-4 text-lg group relative overflow-hidden`}
              whileHover={{ 
                scale: 1.02,
                y: -2
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                window.open('mailto:partnerships@rippleone.com', '_blank');
              }}
            >
              {/* Button shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-500"></div>
              
              <span className="relative z-10 flex items-center space-x-2">
                <span>Become a Partner</span>
                <motion.div
                  className="w-5 h-5"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <svg fill="currentColor" viewBox="0 0 20 20" className="text-slate-700">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </motion.div>
              </span>
            </motion.button>
          ) : (
            /* Mobile version with simplified interactions */
            <button
              className={`${DESIGN_SYSTEM.buttons.secondary} px-12 py-4 text-lg relative overflow-hidden flex items-center space-x-2 mx-auto`}
              onClick={() => {
                window.open('mailto:partnerships@rippleone.com', '_blank');
              }}
            >
              <span>Become a Partner</span>
              <svg fill="currentColor" viewBox="0 0 20 20" className="text-slate-700 w-5 h-5">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Partners; 